// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Haggle — the screens that are not the table.

import { MODES, MODE_IDS, type ModeId } from './modes';
import { TIERS, TIER_IDS, type TierId } from './bot';
import { BOUNTY_TOTAL, TARIFF_TOTAL, WARE_NAME } from './wares';
import { WARE_GLYPH } from './palette';
import type { Summary } from './view';
import type { Seat } from './rules';

const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

export const FOOTER_HTML = `<footer class="site-footer">Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a></footer>`;

export const HOW_HTML = `
  <h2>How to play</h2>
  <ol>
    <li><strong>Your turn is exactly one of:</strong> <strong>TAKE</strong> a ware ·
      <strong>HIRE</strong> every porter on the trestle · <strong>BARTER</strong> 2+ of your cards for
      that many wares · <strong>SELL</strong>.</li>
    <li>Selling N of one ware pays the top N chits off its tariff —
      <strong>and a tariff only ever falls</strong>. The next three prices are printed under each one,
      so you can read your own haircut coming.</li>
    <li>Sell <strong>3+ cards for 10+ chits</strong> to also claim the top <strong>bounty</strong>.
      Copper, Glass and Resin sell two or more at a time.</li>
    <li><strong>Three empty tariffs ends the deal.</strong> Most chits wins it, +6 for the bigger gang,
      +2 to whoever did not start. First to two pennants takes the match.</li>
  </ol>
  <p>Tap a berth to mark a card <strong>in</strong>; tap your own stacks or your porters to mark cards
  <strong>out</strong>. One in and nothing out is a take. Nothing in and a stack out is a sale. Two or
  more of each, and it is a barter. The button always shows exactly what you are about to do.</p>
  <p>On a keyboard: <strong>1–5</strong> berths, <strong>Q W E R T</strong> your stacks,
  <strong>Space</strong> to commit, <strong>Esc</strong> to clear.</p>`;

export function aboutHtml(): string {
  return `
  <h2>About</h2>
  <p><strong>Haggle</strong> is a two-player market duel about one idea: the price of a ware is not a
  number somebody chose, it is a column of chits you and your rival both draw from. Every turn asks
  whether to bank a small sale now or hold for a bigger one while the price falls under you.</p>
  <p>A deal is worth exactly ${TARIFF_TOTAL} chits on the tariffs and ${BOUNTY_TOTAL} in bounties.
  Whatever neither of you banks is simply gone — which is what the summary shows you at the end.</p>
  <p>Everything runs in your browser. There is no account, no server and no game state anywhere but
  the two devices playing. Playing with a friend connects the two browsers directly
  (peer-to-peer); a public relay is used only to introduce them, and no gameplay is stored on it.</p>
  <p>No cookies, no fingerprinting, no third-party fonts. Anonymous, cookie-less page-view counts via
  Cloudflare Web Analytics.</p>
  <p>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
  · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp;
  sites</a></p>`;
}

export function menuHtml(mode: ModeId, tier: TierId, muted: boolean, best: number): string {
  return `
  <div class="main-content">
    <div class="brand">
      <h1>HAGGLE</h1>
      <p>Five wares, one plank table, and a price that only ever falls — sell before your rival does.</p>
    </div>

    <div class="field">
      <div class="label">Market</div>
      <div class="segmented">${MODE_IDS.map(
        (id) =>
          `<button type="button" class="seg${id === mode ? ' on' : ''}" data-mode="${id}">${MODES[id].name}</button>`,
      ).join('')}</div>
      <p class="fine">${esc(MODES[mode].blurb)}</p>
    </div>

    <div class="field">
      <div class="label">Opponent</div>
      <div class="segmented">${TIER_IDS.map(
        (id) =>
          `<button type="button" class="seg${id === tier ? ' on' : ''}" data-tier="${id}">${TIERS[id].name}</button>`,
      ).join('')}</div>
      <p class="fine">${esc(TIERS[tier].blurb)}</p>
    </div>

    <div class="menu">
      <button class="btn primary" id="playSolo" type="button">Play</button>
      <button class="btn" id="playFriends" type="button">Play with a friend</button>
      <button class="btn ghost" id="howBtn" type="button">How to play</button>
      <button class="btn ghost" id="aboutBtn" type="button">About</button>
      <button class="btn ghost" id="muteBtn" type="button" aria-pressed="${muted}">${muted ? 'Sound off' : 'Sound on'}</button>
    </div>
    ${best > 0 ? `<p class="fine">Best deal on ${MODES[mode].name}: <strong>${best}</strong> chits.</p>` : ''}
  </div>
  ${FOOTER_HTML}`;
}

/**
 * THE DISMISSAL CONTRACT. Four exits, all four shipped: a 44px close control, a tap anywhere
 * outside the panel, Escape, and a backdrop that is genuinely reachable. The outside-tap listener is
 * armed 350ms late on purpose — a panel opened from a tap is otherwise closed by that same tap's own
 * trailing click, which reads as a button that does nothing at all.
 */
export function openPanel(host: HTMLElement, html: string): void {
  const back = document.createElement('div');
  back.className = 'backdrop';
  back.innerHTML = `<div class="sheet" role="dialog" aria-modal="true">
    <button class="closex" type="button" aria-label="Close">✕</button>${html}</div>`;
  host.append(back);
  const sheet = back.querySelector<HTMLElement>('.sheet')!;
  let armed = false;
  const close = (): void => {
    document.removeEventListener('keydown', onKey);
    back.remove();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  back.querySelector<HTMLElement>('.closex')!.addEventListener('click', close);
  back.addEventListener('pointerdown', (e) => {
    if (!armed) return;
    if (!sheet.contains(e.target as Node)) close();
  });
  document.addEventListener('keydown', onKey);
  setTimeout(() => {
    armed = true;
  }, 350);
}

export function summaryHtml(
  s: Summary,
  you: Seat,
  pennants: readonly [number, number],
  matchLine: string,
  multiplayer: boolean,
): string {
  const line = (i: 0 | 1): string => {
    const l = s.lines[i];
    const mine = l.seat === you;
    return `<div class="pline ${l.won ? 'win' : ''}">
      <div class="top">
        <span class="nm">${esc(l.name)}${mine ? ' (you)' : ''}</span>
        ${l.won ? '<span aria-label="winner">★</span>' : ''}
        <span class="tot">${l.score.total}</span>
      </div>
      <div class="parts">${l.score.chits} in chits${l.score.bounty ? ` · ${l.score.bounty} bounty` : ''}${
        l.score.hire ? ` · +${l.score.hire} bigger gang` : ''
      }${l.score.tare ? ` · +${l.score.tare} tare` : ''} · ${l.gang} porters · ${l.stranded} card${
      l.stranded === 1 ? '' : 's'
    } left unsold</div>
      <div class="sales">${
        l.sales.length === 0
          ? '<span class="parts">Sold nothing at all.</span>'
          : l.sales
              .map(
                (w) =>
                  `<span class="sc" style="background:var(--w-${w.ware})">${WARE_GLYPH[w.ware]} ${w.cards}× ${WARE_NAME[w.ware]} = ${w.chits}${w.best !== w.chits ? ` (best ${w.best})` : ''}</span>`,
              )
              .join('')
      }</div>
    </div>`;
  };
  return `<div class="main-content results">
    <h2>${s.lines[you].won ? 'You take the deal.' : 'Your rival takes the deal.'}</h2>
    <p class="res-sub">${esc(s.reason)} ${esc(matchLine)}</p>
    <div class="plines">${line(0)}${line(1)}</div>
    <div class="missed">Between you, <strong>${s.unclaimed} chits</strong> of the
      ${TARIFF_TOTAL + BOUNTY_TOTAL} on the table went unclaimed${
        s.burned > 0 ? `, and the bell burned ${s.burned} of them` : ''
      }. Every card still in a satchel at the end was worth nothing.</div>
    <div class="res-actions">
      <button class="btn primary" id="againBtn" type="button">Play again</button>
      <button class="btn" id="lobbyBtn" type="button" hidden>Back to the lobby</button>
      <button class="btn ghost" id="menuBtn" type="button">${multiplayer ? 'Leave the room' : 'Menu'}</button>
      <p class="res-wait" id="resWait" hidden></p>
      <button class="results-feedback" type="button">Something wrong? Tell me</button>
    </div>
    <p class="fine">Pennants: you ${pennants[you]} — ${pennants[you === 0 ? 1 : 0]} them.</p>
  </div>
  ${FOOTER_HTML}`;
}
